import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, ShieldWarning } from "@phosphor-icons/react";

const STATES = ["TX", "FL", "GA", "NC", "SC", "TN", "OH", "AL"];

export default function ZillowSniper({ onScanned }) {
  const [state, setState] = useState("TX");
  const [searchUrl, setSearchUrl] = useState("");
  const [pasteHtml, setPasteHtml] = useState("");
  const [keywords, setKeywords] = useState("");
  const [defaults, setDefaults] = useState([]);
  const [minDays, setMinDays] = useState(60);
  const [propertyType, setPropertyType] = useState("Residential");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get("/zillow/keywords").then(r => setDefaults(r.data.defaults || [])).catch(() => {});
  }, []);

  const run = async (e) => {
    e.preventDefault();
    if (!searchUrl.trim() && !pasteHtml.trim()) {
      toast.error("Paste a Zillow search URL or paste the page HTML.");
      return;
    }
    setBusy(true);
    try {
      const kws = (keywords || "").split(/[\n,]/).map(s => s.trim()).filter(Boolean);
      const r = await api.post("/zillow/scrape", {
        search_url: searchUrl.trim() || null,
        paste_html: pasteHtml.trim() || null,
        state,
        keywords: kws.length ? kws : null,
        min_days_on_market: Number(minDays) || 0,
        property_type: propertyType,
      });
      setLastResult(r.data);
      const { inserted, scanned, error } = r.data;
      if (error) toast.warning(`Zillow: ${error}`);
      else toast.success(`Zillow scan: ${inserted} inserted · ${scanned} scanned`);
      onScanned?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scrape failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-violet-500/40 rounded-sm bg-violet-500/[0.03] p-4 mb-6" data-testid="zillow-sniper">
      <div className="flex items-start gap-2 mb-3">
        <MagnifyingGlass size={16} className="text-violet-300 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-violet-200">Zillow Sniper</div>
          <div className="text-[11px] text-zinc-400">Pull FSBO + aged listings, filter for motivated-seller keywords, extract phone numbers from the description. Skip-trace is paused until you click <span className="mono">Buy Contact</span> on each lead.</div>
        </div>
      </div>

      <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/40 px-3 py-2 rounded-sm mb-3 flex items-start gap-2">
        <ShieldWarning size={13} className="shrink-0 mt-0.5" />
        <div>
          Zillow actively blocks server-side scrapers. If the URL fetch returns HTTP 403 or 999, open the search in your browser, <span className="mono">View → Developer → View Source</span>, copy everything, and paste it into the HTML fallback field.
        </div>
      </div>

      <form onSubmit={run} className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <div className="overline mb-1">State</div>
            <select value={state} onChange={e => setState(e.target.value)} data-testid="zillow-state"
              className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-violet-500">
              {STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div className="overline mb-1">Property type</div>
            <select value={propertyType} onChange={e => setPropertyType(e.target.value)} data-testid="zillow-ptype"
              className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-violet-500">
              <option>Residential</option>
              <option>Land</option>
              <option>Commercial</option>
            </select>
          </div>
          <div>
            <div className="overline mb-1">Min days on market</div>
            <input type="number" value={minDays} onChange={e => setMinDays(e.target.value)} data-testid="zillow-min-days"
              className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-violet-500" />
          </div>
        </div>

        <div>
          <div className="overline mb-1">Zillow search URL</div>
          <input type="url" value={searchUrl} onChange={e => setSearchUrl(e.target.value)} data-testid="zillow-url"
            placeholder="https://www.zillow.com/homes/fsbo/TX/"
            className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-violet-500" />
        </div>

        <div>
          <div className="overline mb-1">Motivated keywords (comma or newline separated)</div>
          <input type="text" value={keywords} onChange={e => setKeywords(e.target.value)} data-testid="zillow-keywords"
            placeholder={defaults.slice(0, 6).join(", ") || "must sell, cash offer, fixer-upper, as-is, motivated"}
            className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-violet-500" />
          <div className="text-[10px] text-zinc-500 mono mt-1">Leave blank to use defaults: {defaults.slice(0, 4).join(", ")}…</div>
        </div>

        <details className="border border-zinc-800 rounded-sm">
          <summary className="px-3 py-2 text-[11px] mono uppercase tracking-[0.15em] text-zinc-400 cursor-pointer">Paste HTML fallback (if URL is blocked)</summary>
          <div className="p-3 border-t border-zinc-800">
            <textarea value={pasteHtml} onChange={e => setPasteHtml(e.target.value)} rows={4} data-testid="zillow-paste-html"
              placeholder="Paste the full Zillow search-page HTML here…"
              className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-[10px] rounded-sm focus:outline-none focus:border-violet-500" />
          </div>
        </details>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={busy} data-testid="zillow-run"
            className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm bg-violet-500 hover:bg-violet-400 text-white font-semibold flex items-center gap-2 disabled:opacity-50">
            <MagnifyingGlass size={13} weight="bold" /> {busy ? "Scraping…" : "Run Zillow scan"}
          </button>
          {lastResult && (
            <div className="text-[11px] mono text-zinc-400" data-testid="zillow-last-result">
              Last: <span className="text-emerald-400">{lastResult.inserted} inserted</span> · {lastResult.scanned} scanned ·
              {" "}dupes {lastResult.skipped?.duplicates} · kw-miss {lastResult.skipped?.keyword_no_match} · new {lastResult.skipped?.too_new}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
